import * as G6 from '@antv/g6';
import { Color, Spacing } from '@persona-id/design-tokens';
import { throttle } from 'lodash/fp';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import NodeMouseEnterEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-enter-event';
import NodeMouseLeaveEvent from 'dashboard/components/GraphCanvasV2/events/node-mouse-leave-event';
import NodePositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/node-position-change';
import RendererAfterAnimateEvent from 'dashboard/components/GraphCanvasV2/events/renderer-after-animate-event';
import RendererBeforeAnimateEvent from 'dashboard/components/GraphCanvasV2/events/renderer-before-animate-event';
import RendererBeforeLayoutChange from 'dashboard/components/GraphCanvasV2/events/renderer-before-layout-change-event';
import RendererLayoutChangeEvent from 'dashboard/components/GraphCanvasV2/events/renderer-layout-change-event';
import RendererPaintEvent from 'dashboard/components/GraphCanvasV2/events/renderer-paint-event';
import SelectionBoxPositionChangeEvent from 'dashboard/components/GraphCanvasV2/events/selection-box-position-change-event';
import { useEventBus } from 'dashboard/components/GraphModule/hooks/useEventBus';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import {
  CollapsedGroup,
  CollapsedGroupId,
  CollapsedPairwiseNode,
  CollapsedPairwiseNodeId,
  HashableNodeId,
  Node,
  NodeId,
  nodeIdFromHashable,
} from 'dashboard/models/graph';
import { Z_INDICES } from 'dashboard/styles/variables';
import { Elevation } from 'design-system/variables/effect';
import { SegmentClient } from 'lib/clients/SegmentClient';

import CollapsedGroupMouseEnterEvent from './events/collapsed-group-mouse-enter-event';
import CollapsedGroupMouseLeaveEvent from './events/collapsed-group-mouse-leave-event';
import CollapsedGroupPositionChangeEvent from './events/collapsed-group-position-change';
import CollapsedPairwiseNodeMouseEnterEvent from './events/collapsed-pairwise-node-mouse-enter-event';
import CollapsedPairwiseNodeMouseLeaveEvent from './events/collapsed-pairwise-node-mouse-leave-event';
import CollapsedPairwiseNodePositionChangeEvent from './events/collapsed-pairwise-node-position-change';

// TODO: Only apply this global style when the mouse is within
// the canvas
const StopBackScroll = createGlobalStyle`
  html {
    overscroll-behavior-x: none;
  }
`;

const ActionBar = styled.div<{ x: number; y: number }>`
  position: absolute;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  width: fit-content;
  background: ${Color.PrimaryWhite};
  box-shadow: ${Elevation.High};
  border-radius: ${Spacing[2]};
`;

const ActionsContainer = styled.div`
  margin: ${Spacing[3]};
  z-index: ${Z_INDICES.floatingBar};
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${Spacing[2]};
`;

const GlobalLiveStyleHack = createGlobalStyle<{ liveMode: boolean }>`
  .g6-grid {
    background-color: ${({ liveMode }) => (liveMode ? Color.CoreBlue800 : 'none')};
    transition: background-color 1.0s ease-in-out;
  }
`;

const LiveIndicator = styled.div`
  position: absolute;
  left: 40px;
  top: 80px;
  border-radius: 3px;
  padding: ${Spacing[1]} ${Spacing[4]};
  color: black;
  opacity: 0;

  &.show {
    opacity: 1;
  }

  @keyframes blink {
    0% {
      background-color: rgba(255, 209, 82, 1);
    }
    50% {
      background-color: rgba(255, 209, 82, 0.4);
    }
    100% {
      background-color: rgba(255, 209, 82, 1);
    }
  }
  @-webkit-keyframes blink {
    0% {
      background-color: rgba(255, 209, 82, 1);
    }
    50% {
      background-color: rgba(255, 209, 82, 0.4);
    }
    100% {
      background-color: rgba(255, 209, 82, 1);
    }
  }

  & {
    transition: all 0.5s ease-in-out;
    animation: blink normal 1.5s infinite ease-in-out;
  }
`;

export interface GraphCanvasV2Props {
  handleG6Mount: () => void;
  height: number;
  selectedNodes: NodeId[];
  // TODO: Change this () => Config
  selectionActions?: () => React.ReactElement[];
  width: number;
}

const GraphCanvasV2 = ({
  handleG6Mount,
  height,
  selectedNodes,
  selectionActions,
  width,
}: GraphCanvasV2Props) => {
  const containerRef = useRef<HTMLDivElement>();
  const actionBarEl = useRef<HTMLDivElement>(null);
  const { graphExplorer } = useGraphExplorer();
  const g6Wrapper = useG6Wrapper();
  const eventBus = useEventBus();
  const { showLiveIndicator } = useGraphQuerying();

  useEffect(() => {
    g6Wrapper.changeSize(width, height);
  }, [g6Wrapper, width, height]);

  const handleNodeHoverStart = useCallback(
    ({ graphTarget: node }: NodeMouseEnterEvent) => {
      // Don't show highlight for dimmed nodes
      if (!graphExplorer.isDimmed(node.id)) {
        graphExplorer.setHighlightedNodes([(node as Node).id]);
      }
      SegmentClient.sendSegmentTrackEvent('Graph - Hover Individual');
    },
    [graphExplorer],
  );

  const handleNodeHoverEnd = useCallback(
    ({ graphTarget: _node }: NodeMouseLeaveEvent) => {
      graphExplorer.setHighlightedNodes([]);
      SegmentClient.sendSegmentTrackEvent('Graph - Unhover Individual');
    },
    [graphExplorer],
  );

  const handleNodePositionChange = useCallback(
    ({ graphTarget: nodePositionChanges }: NodePositionChangeEvent) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Object.keys(nodePositionChanges).forEach((hashableNodeId: HashableNodeId) => {
        const { x, y } = nodePositionChanges[hashableNodeId];
        g6Wrapper.setNodePosition(nodeIdFromHashable(hashableNodeId), x, y);
      });
    },
    [graphExplorer],
  );

  const handleCollapsedGroupHoverStart = useCallback(
    ({ graphTarget: collapsedGroup }: CollapsedGroupMouseEnterEvent) => {
      graphExplorer.highlightCollapsedGroup((collapsedGroup as CollapsedGroup).groupId, true);
      SegmentClient.sendSegmentTrackEvent('Graph - Hover Collapsed Group');
    },
    [graphExplorer],
  );

  const handleCollapsedGroupHoverEnd = useCallback(
    ({ graphTarget: collapsedGroup }: CollapsedGroupMouseLeaveEvent) => {
      graphExplorer.highlightCollapsedGroup((collapsedGroup as CollapsedGroup).groupId, false);
      SegmentClient.sendSegmentTrackEvent('Graph - Unhover Collapsed Group');
    },
    [graphExplorer],
  );

  const handleCollapsedPairwiseNodeHoverStart = useCallback(
    ({ graphTarget: collapsedPairwiseNode }: CollapsedPairwiseNodeMouseEnterEvent) => {
      graphExplorer.highlightCollapsedPairwiseNode(
        (collapsedPairwiseNode as CollapsedPairwiseNode).id.value,
        true,
      );
      SegmentClient.sendSegmentTrackEvent('Graph - Hover Collapsed Pairwise Node');
    },
    [graphExplorer],
  );

  const handleCollapsedPairwiseNodeHoverEnd = useCallback(
    ({ graphTarget: collapsedPairwiseNode }: CollapsedPairwiseNodeMouseLeaveEvent) => {
      graphExplorer.highlightCollapsedPairwiseNode(
        (collapsedPairwiseNode as CollapsedPairwiseNode).id.value,
        false,
      );
      SegmentClient.sendSegmentTrackEvent('Graph - Unhover Collapsed Pairwise Node');
    },
    [graphExplorer],
  );

  const handleCollapsedGroupPositionChange = useCallback(
    ({ graphTarget: collapsedGroupPositionChanges }: CollapsedGroupPositionChangeEvent) => {
      Object.keys(collapsedGroupPositionChanges).forEach((collapsedGroupId: CollapsedGroupId) => {
        const { x, y } = collapsedGroupPositionChanges[collapsedGroupId];
        g6Wrapper.setCollapsedGroupPosition(collapsedGroupId, x, y);
      });
    },
    [graphExplorer],
  );

  const handleCollapsedPairwiseNodePositionChange = useCallback(
    ({
      graphTarget: collapsedPairwiseNodePositionChanges,
    }: CollapsedPairwiseNodePositionChangeEvent) => {
      Object.keys(collapsedPairwiseNodePositionChanges).forEach(
        (collapsedPairwiseNodeId: CollapsedPairwiseNodeId) => {
          const { x, y } = collapsedPairwiseNodePositionChanges[collapsedPairwiseNodeId];
          g6Wrapper.setCollapsedPairwiseNodePosition(collapsedPairwiseNodeId, x, y);
        },
      );
    },
    [graphExplorer],
  );

  const [selectionBoxPos, setSelectionBoxPos] = useState<G6.IBBox>({
    x: 0,
    y: 0,
    maxX: 0,
    maxY: 0,
    minX: 0,
    minY: 0,
    centerX: 0,
    centerY: 0,
    width: 0,
    height: 0,
  });
  const handleSelectionBoxPositionChange = useCallback(
    ({ graphTarget: selectionBBox }: SelectionBoxPositionChangeEvent) => {
      setSelectionBoxPos({
        ...selectionBBox,
        centerX: selectionBBox.maxX - selectionBBox.width / 2,
        centerY: selectionBBox.maxY - selectionBBox.height / 2,
      });
    },
    [setSelectionBoxPos],
  );

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(NodeMouseEnterEvent.type, handleNodeHoverStart);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(NodeMouseLeaveEvent.type, handleNodeHoverEnd);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(NodePositionChangeEvent.type, handleNodePositionChange);
    eventBus.addEventListener(
      SelectionBoxPositionChangeEvent.type,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleSelectionBoxPositionChange,
    );
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(CollapsedGroupMouseEnterEvent.type, handleCollapsedGroupHoverStart);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    eventBus.addEventListener(CollapsedGroupMouseLeaveEvent.type, handleCollapsedGroupHoverEnd);
    eventBus.addEventListener(
      CollapsedGroupPositionChangeEvent.type,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCollapsedGroupPositionChange,
    );
    eventBus.addEventListener(
      CollapsedPairwiseNodeMouseEnterEvent.type,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCollapsedPairwiseNodeHoverStart,
    );
    eventBus.addEventListener(
      CollapsedPairwiseNodeMouseLeaveEvent.type,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCollapsedPairwiseNodeHoverEnd,
    );
    eventBus.addEventListener(
      CollapsedPairwiseNodePositionChangeEvent.type,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCollapsedPairwiseNodePositionChange,
    );

    return () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      eventBus.removeEventListener(NodeMouseEnterEvent.type, handleNodeHoverStart);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      eventBus.removeEventListener(NodeMouseLeaveEvent.type, handleNodeHoverEnd);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      eventBus.removeEventListener(NodePositionChangeEvent.type, handleNodePositionChange);
      eventBus.removeEventListener(
        SelectionBoxPositionChangeEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleSelectionBoxPositionChange,
      );
      eventBus.removeEventListener(
        CollapsedGroupMouseEnterEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedGroupHoverStart,
      );
      eventBus.removeEventListener(
        CollapsedGroupMouseLeaveEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedGroupHoverEnd,
      );
      eventBus.removeEventListener(
        CollapsedGroupPositionChangeEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedGroupPositionChange,
      );
      eventBus.removeEventListener(
        CollapsedPairwiseNodeMouseEnterEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedPairwiseNodeHoverStart,
      );
      eventBus.removeEventListener(
        CollapsedPairwiseNodeMouseLeaveEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedPairwiseNodeHoverEnd,
      );
      eventBus.removeEventListener(
        CollapsedPairwiseNodePositionChangeEvent.type,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleCollapsedPairwiseNodePositionChange,
      );
    };
  }, [
    eventBus,
    handleNodeHoverStart,
    handleNodeHoverEnd,
    handleNodePositionChange,
    handleSelectionBoxPositionChange,
    handleCollapsedGroupHoverStart,
    handleCollapsedGroupHoverEnd,
    handleCollapsedPairwiseNodeHoverStart,
    handleCollapsedPairwiseNodeHoverEnd,
    handleCollapsedGroupPositionChange,
    handleCollapsedPairwiseNodePositionChange,
  ]);

  useEffect(() => {
    // Initialize G6 Canvas
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    g6Wrapper.bindToContainer(containerRef.current, width, height);
    // Notify others that we've mounted.
    handleG6Mount();

    g6Wrapper.graph.on(
      'viewportchange',
      throttle(16, (e) => {
        eventBus?.dispatchEvent(
          new CanvasViewportChangeEvent(e as unknown as Event, g6Wrapper.canvas),
        );
      }),
    );

    g6Wrapper.graph.on('beforelayout', (e) => {
      eventBus?.dispatchEvent(
        new RendererBeforeLayoutChange(e as unknown as Event, g6Wrapper.canvas),
      );
    });

    g6Wrapper.graph.on('afterlayout', (e) => {
      eventBus?.dispatchEvent(
        new RendererLayoutChangeEvent(e as unknown as Event, g6Wrapper.canvas),
      );
    });

    g6Wrapper.graph.on('afterpaint', (e) => {
      eventBus?.dispatchEvent(new RendererPaintEvent(e as unknown as Event, g6Wrapper.canvas));
    });

    g6Wrapper.graph.on('beforeanimate', (e) => {
      eventBus?.dispatchEvent(
        new RendererBeforeAnimateEvent(e as unknown as Event, g6Wrapper.canvas),
      );
    });

    g6Wrapper.graph.on('afteranimate', (e) => {
      eventBus?.dispatchEvent(
        new RendererAfterAnimateEvent(e as unknown as Event, g6Wrapper.canvas),
      );
    });

    return () => {
      if (!g6Wrapper.destroyed) g6Wrapper.destroy();
    };
  }, []);

  const [actionBarPos, setActionBarPos] = useState(null);
  useEffect(() => {
    if (!actionBarEl.current || !selectionBoxPos) {
      return;
    }

    setActionBarPos({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      x: selectionBoxPos.centerX - (actionBarEl.current?.offsetWidth || 0) / 2,
      y: selectionBoxPos.maxY + 64,
    });
  }, [actionBarEl.current, selectionBoxPos]);

  return (
    <div>
      <GlobalLiveStyleHack liveMode={showLiveIndicator || false} />
      <StopBackScroll />
      <div
        ref={(el) => {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          containerRef.current = el;
        }}
        style={{
          position: 'relative',
        }}
      >
        {selectionActions && selectedNodes.length > 1 && (
          <ActionBar
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            x={actionBarPos?.x || 0}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            y={actionBarPos?.y || 0}
            ref={actionBarEl}
            id='graph-canvas-action-bar'
          >
            <ActionsContainer>{selectionActions()}</ActionsContainer>
          </ActionBar>
        )}
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <LiveIndicator className={showLiveIndicator ? 'show' : null}>LIVE</LiveIndicator>
      </div>
    </div>
  );
};

export default GraphCanvasV2;
